const INSTITUTIONAL = "institutional";
const INSTITUTIONS = "institutions";

export const NORTH_AMERICA = "na";
export const EUROPE_ASIA = "eua";

export type AudienceType = typeof NORTH_AMERICA | typeof EUROPE_ASIA;

/**
 * UserAudienceInfo defines the structure of the audience information for a user, including their audience type, country, and language.
 */
export interface UserAudienceInfo {
  audience: AudienceType;
  country: string;
  lang: string;
}

/**
 * Maps path names to user audience codes.
 */
export const pathToAudience: { [key: string]: AudienceType } = {
  institutional: NORTH_AMERICA,
  institutions: EUROPE_ASIA,
};

/**
 * Maps user audience codes to path names.
 */
export const audienceToPath: { [key: string]: string } = {
  [NORTH_AMERICA]: INSTITUTIONAL,
  [EUROPE_ASIA]: INSTITUTIONS,
};

/**
 * Valid EUA countries with their supported languages
 */
const EUA_COUNTRIES = new Set(["gb"]);

/**
 * Gets the current user audience with country and language information based on the URL path.
 * @param pathname Optional pathname to parse (defaults to current window.location.pathname)
 * @returns UserAudienceInfo object with audience, country, and language
 */
export const getUserAudience = (pathname?: string): UserAudienceInfo => {
  const path = pathname || window.location.pathname;
  const segments = path.split("/").filter(Boolean); // Split and remove empty segments
  const firstSegment = segments[0];
  const DEFAULT_COUNTRY = "us";
  const DEFAULT_LANG = "en";

  // Default to North America
  if (!firstSegment || firstSegment === INSTITUTIONAL) {
    return {
      audience: NORTH_AMERICA,
      country: DEFAULT_COUNTRY,
      lang: DEFAULT_LANG,
    };
  }

  // Handle EUA paths: /institutions/country/language/...
  if (firstSegment === INSTITUTIONS) {
    const country = segments[1].toLocaleLowerCase();
    const lang = segments[2].toLocaleLowerCase();

    // If we have valid country and language segments
    if (country && lang) {
      return {
        audience: EUROPE_ASIA,
        country: EUA_COUNTRIES.has(country) ? country : "gb", // Default to GB if unknown country
        lang: lang,
      };
    }

    // Default for incomplete EUA paths
    return {
      audience: EUROPE_ASIA,
      country: "gb",
      lang: "en",
    };
  }

  // Default fallback
  return {
    audience: NORTH_AMERICA,
    country: DEFAULT_COUNTRY,
    lang: DEFAULT_LANG,
  };
};

/**
 * Gets the base path for a user's audience context.
 * @param audienceContext
 * @returns The base path string for the user's audience (e.g., "/institutional" or "/institutions/country/lang")
 */
export const getUserAudienceBasePath = (
  audienceContext?: UserAudienceInfo,
): string => {
  const context = audienceContext || getUserAudience();
  const { audience, country, lang } = context;

  if (audience === NORTH_AMERICA) {
    return `/${INSTITUTIONAL}`;
  } else {
    return `/${INSTITUTIONS}/${country}/${lang}`;
  }
};

/**
 * Gets the full path for a given route path based on the user's audience context.
 * @param path The route path (e.g., "/documents")
 * @param audienceContext Optional user audience context. If not provided, it will be derived from the current URL.
 * @returns The full path including audience-specific segments.
 */
export const getUserAudiencePath = (
  path: string,
  audienceContext?: UserAudienceInfo,
): string => {
  const basePath = getUserAudienceBasePath(audienceContext);
  return `${basePath}${path}`;
};

/**
 * Gets the legacy region string for a user's audience context.
 * @param audienceContext Optional user audience context. If not provided, it will be derived from the current URL.
 * @returns The legacy region string ("North_America" or "Europe").
 */
export const getLegacyUserAudienceRegion = (
  audienceContext?: UserAudienceInfo,
): string => {
  const { audience } = audienceContext || getUserAudience();
  return audience === NORTH_AMERICA ? "North_America" : "Europe";
};

/**
 * Gets the legacy base path for a user's audience context.
 * @param audienceContext  Optional user audience context. If not provided, it will be derived from the current URL.
 * @returns  The legacy base path string.
 */
export const getLegacyUserAudienceBasePath = (
  audienceContext?: UserAudienceInfo,
): string => {
  const { audience } = audienceContext || getUserAudience();
  return audience === NORTH_AMERICA ? "/us" : "/eua";
};

/**
 * Gets the full legacy path for a given route path based on the user's audience context.
 * @param path The route path (e.g., "/accounts/getReport/:lob=GIG")
 * @param audienceContext Optional user audience context. If not provided, it will be derived from the current URL.
 * @returns The full legacy path including audience-specific segments.
 */
export const getLegacyUserAudiencePath = (
  path: string,
  audienceContext?: UserAudienceInfo,
): string => {
  const basePath = getLegacyUserAudienceBasePath(audienceContext);
  const region = getLegacyUserAudienceRegion(audienceContext);
  return `${basePath}${path}&region=${region}:/`;
};

// ---------------------------------------------------------------------------
// App locale factory
// ---------------------------------------------------------------------------

/**
 * Creates a type-safe `getAppLocale` function bound to a specific set of locale bundles.
 *
 * Each app calls this once with its own JSON registry, keeping the translation
 * files co-located with the app while sharing the resolution logic centrally.
 *
 * @param registry   - Map of BCP 47 keys (e.g. "en-US") to locale bundle objects.
 * @param defaultKey - The key to fall back to when no match is found (defaults to "en-US").
 * @returns A `getAppLocale(country, lang)` function that resolves a bundle from URL segments.
 *
 * @example
 * // apps/my-app/src/lang/index.ts
 * import enUS from "./en_US.json";
 * import enGB from "./en_GB.json";
 * import { createLocaleRegistry } from "@repo/utils";
 *
 * export const getAppLocale = createLocaleRegistry({ "en-US": enUS, "en-GB": enGB });
 */
export const createLocaleRegistry = <T>(
  registry: Record<string, T>,
  defaultKey = "en-US",
): ((country: string, lang: string) => T) => {
  const defaultBundle = registry[defaultKey] ?? Object.values(registry)[0];

  return (country: string, lang: string): T =>
    registry[`${lang}-${country.toUpperCase()}`] ?? defaultBundle;
};

export default getUserAudience;
